import { ChangeEvent, useEffect, useState } from "react";
import API from "../api/axios";
import { debounce } from "lodash";
import { useCallback } from "react";

export interface UserAutoCompleteProps {
    value: string,
    onChange: (event: UserAutoCompleteChangeEvent) => any,
    placeholder?: string,
}

export type UserAutoCompleteChangeEvent = {
    user: UserResult,
}

export interface UserResult {
    id: number,
    email: string,
    firstName: string,
    lastName: string,
}

export const UserAutoComplete = (props: UserAutoCompleteProps) => {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<UserResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [moreResults, setMoreResults] = useState(false);

    useEffect(() => {
        setSearch(props.value);
        fetchResults(props.value);
    }, [props.value]);

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
        debouncedFetchResults(event.target.value);
        setShowResults(true);
    }

    const fetchResults = (searchText: string) => {
        setMoreResults(false);
        setResults([]);
        setLoading(true);
        const params = { search: searchText };
        API.get('/users', { params }).then(({ data }) => {
            setResults(data.items);
            setLoading(false);
            if (data.lastPage > 1) {
                setMoreResults(true);
            }
        }).catch(err => {
            console.error(err);
            setLoading(false);
            setShowResults(false);
        })
    }

    const debouncedFetchResults = useCallback(debounce((searchText) => fetchResults(searchText), 300), []);

    const handleOnFocus = () => {
        setShowResults(true);
    }

    const selectUser = (user: UserResult) => {
        props.onChange({ user });
        setShowResults(false);
    }

    return (
        <div className="relative">
            <input type="text" value={search} onChange={handleOnChange} onFocus={handleOnFocus} className="mb-0 input-control" placeholder={props.placeholder}/>
            {showResults ? (
                <>
                    <div className="fixed top-0 left-0 w-screen h-screen" onClick={() => { setShowResults(false) }}></div>
                    <div className="absolute max-w-full overflow-y-auto bg-white border shadow min-w-fit w-80 top-10" style={{ maxHeight: '20rem' }}>
                        {results.map((user, index) => (
                            <button key={'autoselect_user_' + index} className="block w-full p-2 text-sm text-left cursor-pointer hover:bg-blue-200" onClick={() => { selectUser(user) }}>
                                {user.firstName} {user.lastName}
                            </button>
                        ))}
                        {!loading && results.length === 0 ? (
                            <p className="p-2 text-sm italic text-gray-400">No results</p>
                        ) : null}
                        {moreResults ? (
                            <p className="p-2 text-sm italic text-gray-400">Enter search for more results...</p>
                        ) : null}
                        {loading ? (
                            <p className="p-2 text-sm italic text-gray-400">Loading...</p>
                        ) : null}
                    </div>
                </>
            ) : null}
        </div>
    );
}

export default UserAutoComplete;